<template>
  <div class="modal__content modal__content--project-team pl0 pr0 pt30 pb20">
    <div class="border-bottom-lightest2 pl30 pr30 mb30">
      <h5 class="text-size-11 text-space-12 text-color-gray-dark text-medium text-uppercase pt2 mb5">add your team members</h5>

      <!-- form -->
      <form class="flex-nowrap flex--align-vertical mb15" @submit.prevent="onConfirm">
        <input class="form-control form-control--small mr10" v-validate="'required|email'" :class="{'has-error': errors.has('email') }"
               type="text" name="email" id="email" v-model.lazy="userInvite.email" placeholder="name@email.com">
        <select name="user-role" id="user-role" v-model="userInvite.selectedRole" class="form-control form-control--small mr10">
          <option v-for="option in userRoles" :value="option.value" :key="option.name">{{option.name}}</option>
        </select>
        <button class="btn btn--plus-green">+</button>
      </form><!-- form END -->

      <p class="text-size-15 text-medium text-height-14 mb25" v-if="userInvite.selectedRole === 'editor'">Editor can view tasks, add new tasks, write comments, see task costs<br class="hidden-xs-down"> and see project balance. Can not top up the balance.</p>
      <p class="text-size-15 text-medium text-height-14 mb25" v-if="userInvite.selectedRole === 'viewer'">Viewer can view tasks, add new tasks and write comments. Can not<br class="hidden-xs-down"> see task costs, see project balance, top up the balance.</p>
    </div>
    <div class="pl30 pr30">
      <h5 class="text-size-11 text-space-12 text-color-gray-dark text-medium text-uppercase">current members</h5>
      <!-- project-users-list -->
      <ul class="project-users-list">
        <li class="flex flex--justify-space-between border-bottom-lightest2 mb10 pb15 pt1" v-for="projectUser in args.projectUsers" :key="projectUser.id">
          <div class="flex flex--align-vertical project-users-list__avatar mr10 pr15">
            <img :src="'/serve/s/' + projectUser.avatarKey + '?w=80&h=80'" class="img-circle img-circle--40 mr10"
                  alt="User Avatar" v-if="projectUser.avatarKey">
            <div class="img-circle img-circle--40 mr10 flex-nowrap flex--align-vertical" v-else>
              <span class="text-color-white text-uppercase text-size-16 text-lh-1 text-center">
                {{projectUser.name | firstCharacter}}{{projectUser.surname | firstCharacter}}
              </span>
            </div>
            <div class="pt5">
              <h5 class="text-size-18 text-medium text-height-1 mb0 text-color-black pt2">{{projectUser.name}} {{userSurname(projectUser.surname)}}</h5>
              <a class="text-size-13 text-height-1" :href="'mailto:' + projectUser.email"
                v-if="checkUserRole(args.role, ['admin', 'projectManager']) || (checkUserRole(args.userProjectRole, ['owner', 'editor', 'viewer']) && checkUserRole(projectUser.userProjectRole, ['owner', 'editor', 'viewer', 'admin', 'projectManager']))">{{projectUser.email}}</a>
            </div>
            <span class="remove icon-remove"
                  @click="$bus.$emit('modalClose') && $bus.$emit('modalShow', ['project-remove-user-modal', {id: projectUser.id, name: projectUser.name}])"
                  v-if="args.userId !== projectUser.id && (checkUserRole(args.role, ['admin', 'projectManager']) || checkUserRole(args.role, ['customer']) && checkUserRole(projectUser.userProjectRole, ['editor', 'viewer']))">
            </span>
          </div>
          <div class="flex flex--align-vertical w-100 w-auto-sm">
            <h5 class="text-medium mb0 ml20-sm text-color-dark-3">{{projectUser.userProjectRoleText}}</h5>
          </div>
        </li>
      </ul><!-- project-users-list END -->
    </div>
  </div>
</template>

<script>
  import CheckUserRoleMixin from 'modules/mixins/CheckUserRole'
  import { mapActions } from 'vuex'
  import { errorMessage } from 'modules/Error'
  import { apiProjectUserInvite } from '@/api/project.api'

  export default {
    mixins: [CheckUserRoleMixin],

    data () {
      return {
        roles: [
          {
            name: 'Designer',
            value: 'designer',
            canBeSelectedBy: ['admin', 'projectManager']
          },
          {
            name: 'Project Manager',
            value: 'projectManager',
            canBeSelectedBy: ['admin', 'projectManager']
          },
          {
            name: 'Editor',
            value: 'editor',
            canBeSelectedBy: ['admin', 'projectManager', 'customer', 'editor']
          },
          {
            name: 'Viewer',
            value: 'viewer',
            canBeSelectedBy: ['admin', 'projectManager', 'customer', 'editor']
          }
        ],
        userInvite: {
          email: null,
          selectedRole: null
        },
        currentUserRole: null
      }
    },

    props: ['args'],

    computed: {
      userRoles () {
        return _.filter(this.roles, role => {
          return _.includes(role.canBeSelectedBy, this.currentUserRole)
        })
      }

    },

    methods: {
      ...mapActions([
        'addUserToProject'
      ]),

      init () {
        if (this.args.role === 'customer') {
          this.userInvite.selectedRole = 'viewer'
        } else {
          this.userInvite.selectedRole = 'designer'
        }

        if (this.args.projectRole === 'editor') {
          this.currentUserRole = this.args.projectRole
        } else {
          this.currentUserRole = this.args.role
        }
      },

      async onConfirm () {
        this.$bus.$emit('loaderShow')
        const projectUser = {
          role: this.userInvite.selectedRole,
          email: this.userInvite.email,
          projectId: this.$route.params.id
        }

        const isValid = await this.$validator.validateAll()
        if (!isValid) return this.$bus.$emit('loaderClose')
        
        const [err, data] = await apiProjectUserInvite(projectUser)
        this.$bus.$emit('loaderClose')
        if (err) return errorMessage(err, 'Unable to add user to project. Try again later!')

        if (data.invitedExistingUser) {
          const newUser = {...data.invitedExistingUser}
          newUser.userProjectRole = this.userInvite.selectedRole
          this.addUserToProject(newUser)
        }
        
        this.$bus.$emit('modalClose')
        return this.$bus.$emit('toastSuccess', 'User ' + this.userInvite.email + ' was successfully invited')
      },

      userSurname (surname) {
        return this.checkUserRole(this.args.role, ['admin']) ? surname : surname.slice(0, 1) + "."
      }
    },

    created () {
      this.init()
    }
  }
</script>



// WEBPACK FOOTER //
// ProjectTeamModal.vue?9bc947cc